import { Divider, Flex, Tooltip } from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';

import {
  ShareFundingComponentFragmentGQLType,
  ValleyIbsApiRecordFragmentGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { Icon } from '~/packages/common-frontend/components/icons';
import { DEFAULT_SHORT_LOCALE_OPTIONS } from '~/packages/common-frontend/components/Timestamp';
import { Typography } from '~/packages/common-frontend/components/Typography';

interface IbsApiTooltipContentProps {
  ibsApiRecords: ValleyIbsApiRecordFragmentGQLType[];
  targetIbsApiRecord: ValleyIbsApiRecordFragmentGQLType | null;
  fundingComponent?: ShareFundingComponentFragmentGQLType;
  initialLabel: string;
  missingLabel?: string;
  children: React.ReactNode;
}
export const IbsApiTooltip = ({
  ibsApiRecords,
  targetIbsApiRecord,
  fundingComponent,
  initialLabel,
  missingLabel,
  children,
}: IbsApiTooltipContentProps) => {
  const executedIbsApiRecords = useMemo(() => {
    const executedRecords: {
      id: string;
      executedDateStr: string;
      displayStatus: React.ReactNode;
    }[] = [];

    for (const record of ibsApiRecords) {
      const { executedAt, status, id } = record;
      if (!executedAt || status === 'pending') continue;

      const executedDateTime = DateTime.fromMillis(executedAt);
      const executedDateStr = executedDateTime.toLocaleString(
        DEFAULT_SHORT_LOCALE_OPTIONS,
      );

      const displayStatus =
        status === 'executed' ? (
          <Flex alignItems="center" gap="0.5rem">
            <Icon icon="circle-check" color="green.300" />
            <Typography TextProps={{ sx: { color: 'white' }, as: 'span' }}>
              Success
            </Typography>
          </Flex>
        ) : (
          <Flex alignItems="center" gap="0.5rem">
            <Icon icon="circle-x" color="red.400" />
            <Typography TextProps={{ sx: { color: 'white' }, as: 'span' }}>
              Fail
            </Typography>
          </Flex>
        );

      executedRecords.push({ id, executedDateStr, displayStatus });
    }

    return executedRecords;
  }, [ibsApiRecords]);

  if (fundingComponent && !fundingComponent.fundTransferDate) {
    return (
      <Tooltip label="Mark as funded before syncing to IBS">{children}</Tooltip>
    );
  }

  // If no executed records yet, then this would be the initial "pending"
  // record, if it exists.
  if (executedIbsApiRecords.length === 0) {
    // If record is present and status is "pending", then this is the
    // initial one and can provide the initial label.
    if (targetIbsApiRecord?.status === 'pending') {
      return <Tooltip label={initialLabel}>{children}</Tooltip>;
    }

    if (missingLabel) {
      return <Tooltip label={missingLabel}>{children}</Tooltip>;
    }

    return children;
  }

  // Records of this particular IBS API domain have been executed before
  // so showing the history of the executions in the tooltip
  return (
    <Tooltip
      label={
        <Flex flexDirection="column" gap="0.5rem" padding="0.5rem" w="17rem">
          <Flex justifyContent="space-between">
            <Typography TextProps={{ sx: { color: 'white' }, as: 'span' }}>
              Last IBS Sync
            </Typography>
            <Typography
              TextProps={{
                textAlign: 'right',
                sx: { color: 'white' },
                as: 'span',
              }}
            >
              Status
            </Typography>
          </Flex>

          <Divider />

          {executedIbsApiRecords.map((ibsApiRecord) => (
            <Flex key={ibsApiRecord.id} justifyContent="space-between">
              <Typography TextProps={{ sx: { color: 'white' }, as: 'span' }}>
                {ibsApiRecord.executedDateStr}
              </Typography>
              <Typography
                TextProps={{
                  textAlign: 'right',
                  sx: { color: 'white' },
                  as: 'span',
                }}
              >
                {ibsApiRecord.displayStatus}
              </Typography>
            </Flex>
          ))}
        </Flex>
      }
    >
      {children}
    </Tooltip>
  );
};
